import { QueryClient } from '@tanstack/react-query';

/**
 * Single QueryClient for the whole app.
 *
 * Tuned for a client that may be offline at any moment (the packaged app and the
 * installed PWA both are, regularly):
 *
 * - `networkMode: 'offlineFirst'` — run the query even when the browser reports
 *   no connection. A cached result is served immediately and the fetch is only
 *   attempted once; without this, react-query would park the query as `paused`
 *   and screens that gate on `isLoading` would hang on a cold offline launch.
 * - `gcTime` 30 days — a cache entry must outlive the persisted snapshot it was
 *   written into (see lib/persist.ts), otherwise it is collected on rehydrate
 *   and the offline read finds nothing.
 * - `staleTime` 30s + refetch on focus/reconnect — this is the "the app updates
 *   itself" half: coming back to the app, or regaining signal, silently pulls
 *   whatever changed on the server.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 30 * 24 * 60 * 60 * 1000,
      networkMode: 'offlineFirst',
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: (failureCount, error) => {
        // Don't retry on 4xx (auth, validation, not-found) — only on transient 5xx/network.
        if (
          error instanceof Error &&
          'status' in error &&
          typeof (error as { status?: number }).status === 'number'
        ) {
          const s = (error as { status: number }).status;
          if (s >= 400 && s < 500) return false;
        }
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
      // Same reasoning as queries: attempt the write, surface the failure to the
      // caller, rather than silently queueing it with no UI to explain the wait.
      networkMode: 'offlineFirst',
    },
  },
});
